import React, { useCallback, useDeferredValue, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { OneLineInput } from '../⚛️atoms/OneLineInput';
import { deleteDataAPI, getDataAPI, patchDataAPI, postDataAPI } from '../../Helpers/FetchAPI';
import toast from 'react-hot-toast';
import { Accordion } from '@szhsin/react-accordion';
import { WorkbookV2Chapter } from './WorkbookV2Chapter';
import { SortableList } from '../❉molecules/SortableList/SortableList';
import { ONE_TOAST_ID } from '../../constant';
import { ArrowRightIcon, PlusCircleIcon } from '@heroicons/react/24/solid';
import Dropzone from 'react-dropzone';
import { PencilSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';
import placeholder from '../../images/pd-resource-square.png';
import { SimpleModal } from '../❉molecules/SimpleModal';
import { useParams } from 'react-router-dom';
import { useNav } from '../../Context/Navigation';
import { Button } from '../⚛️atoms/Button';
import Loading from '../Loading';
import { updateTranslation } from '../../utils/translationHelper';
import {
  buildWorkbookV2SavePlan,
  buildWorkbookV2ItemCreatePayload,
  cloneWorkbookV2Value,
  createWorkbookV2ChapterDraft,
  createWorkbookV2ItemDraft,
  createWorkbookV2LessonDraft,
  isWorkbookV2TempId,
  mergeTranslatedLessonWithDraft,
  workbookV2EditorReducer
} from './editorUtils';

const extractLocales = (translations = {}) => {
  const locales = new Set();

  Object.values(translations).forEach((entries) => {
    (entries ?? []).forEach(({ locale }) => {
      if (locale) {
        locales.add(locale);
      }
    });
  });

  return [...locales];
};

const normalizeWorkbookV2 = (data) =>
  cloneWorkbookV2Value({
    ...data,
    translations: data.translations ?? {},
    chapters: data.chapters ?? []
  });

const updateChapterById = (chapters = [], chapterId, updater) =>
  chapters.map((chapter) => (chapter._id === chapterId ? updater(chapter) : chapter));

const updateLessonById = (chapters = [], chapterId, lessonId, updater) =>
  chapters.map((chapter) =>
    chapter._id !== chapterId
      ? chapter
      : {
          ...chapter,
          lessons: chapter.lessons.map((lesson) => (lesson._id === lessonId ? updater(lesson) : lesson))
        }
  );

const updateItemById = (chapters = [], chapterId, lessonId, itemId, updater) =>
  chapters.map((chapter) =>
    chapter._id !== chapterId
      ? chapter
      : {
          ...chapter,
          lessons: chapter.lessons.map((lesson) =>
            lesson._id !== lessonId
              ? lesson
              : {
                  ...lesson,
                  items: lesson.items.map((item) => (item._id === itemId ? updater(item) : item))
                }
          )
        }
  );

const getChapterById = (workbook, chapterId) =>
  (workbook?.chapters ?? []).find((chapter) => chapter._id === chapterId) ?? null;

const getLessonById = (workbook, chapterId, lessonId) =>
  getChapterById(workbook, chapterId)?.lessons?.find((lesson) => lesson._id === lessonId) ?? null;

const setWorkbookChapterLessons = (workbook, chapterId, lessons) => ({
  ...workbook,
  chapters: updateChapterById(workbook.chapters ?? [], chapterId, (chapter) => ({
    ...chapter,
    lessons: cloneWorkbookV2Value(lessons)
  }))
});

const setWorkbookLessonItems = (workbook, chapterId, lessonId, items) => ({
  ...workbook,
  chapters: updateLessonById(workbook.chapters ?? [], chapterId, lessonId, (lesson) => ({
    ...lesson,
    items: cloneWorkbookV2Value(items)
  }))
});

const removeWorkbookChapter = (workbook, chapterId) => ({
  ...workbook,
  chapters: (workbook.chapters ?? []).filter((chapter) => chapter._id !== chapterId)
});

const removeWorkbookLesson = (workbook, chapterId, lessonId) => ({
  ...workbook,
  chapters: updateChapterById(workbook.chapters ?? [], chapterId, (chapter) => ({
    ...chapter,
    lessons: (chapter.lessons ?? []).filter((lesson) => lesson._id !== lessonId)
  }))
});

const removeWorkbookItem = (workbook, chapterId, lessonId, itemId) => ({
  ...workbook,
  chapters: updateLessonById(workbook.chapters ?? [], chapterId, lessonId, (lesson) => ({
    ...lesson,
    items: (lesson.items ?? []).filter((item) => item._id !== itemId)
  }))
});

const replaceWorkbookTempChapter = (workbook, tempChapterId, serverChapter) => ({
  ...workbook,
  chapters: (workbook.chapters ?? []).map((chapter) =>
    chapter._id !== tempChapterId
      ? chapter
      : {
          ...cloneWorkbookV2Value(serverChapter),
          ...cloneWorkbookV2Value(chapter),
          _id: serverChapter._id,
          lessons: cloneWorkbookV2Value(chapter.lessons ?? serverChapter.lessons ?? [])
        }
  )
});

const replaceWorkbookTempLesson = (workbook, chapterId, tempLessonId, serverLesson) => ({
  ...workbook,
  chapters: updateChapterById(workbook.chapters ?? [], chapterId, (chapter) => ({
    ...chapter,
    lessons: (chapter.lessons ?? []).map((lesson) =>
      lesson._id !== tempLessonId
        ? lesson
        : {
            ...cloneWorkbookV2Value(serverLesson),
            ...cloneWorkbookV2Value(lesson),
            _id: serverLesson._id,
            items: cloneWorkbookV2Value(lesson.items ?? serverLesson.items ?? [])
          }
    )
  }))
});

const replaceWorkbookTempItem = (workbook, chapterId, lessonId, tempItemId, serverItem) => ({
  ...workbook,
  chapters: updateLessonById(workbook.chapters ?? [], chapterId, lessonId, (lesson) => ({
    ...lesson,
    items: (lesson.items ?? []).map((item) =>
      item._id !== tempItemId
        ? item
        : {
            ...cloneWorkbookV2Value(serverItem),
            ...cloneWorkbookV2Value(item),
            _id: serverItem._id
          }
    )
  }))
});

const findCreatedEntity = (serverEntities = [], existingEntities = []) => {
  const existingIds = new Set((existingEntities ?? []).map(({ _id }) => _id));
  return serverEntities.find(({ _id }) => !existingIds.has(_id)) ?? null;
};

export const WorkbookEditorV2 = () => {
  const urlParams = useParams();
  const { setCurrentPage, setForceCollapse } = useNav();
  const [draft, dispatch] = useReducer(workbookV2EditorReducer, null);
  const [editImage, setEditImage] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [locales, setLocales] = useState([]);
  const [editingLanguage, setEditingLanguage] = useState(null);
  const [pendingLocale, setPendingLocale] = useState('');
  const [showNewTranslationModal, setShowNewTranslationModal] = useState(false);
  const [snapshotVersion, setSnapshotVersion] = useState(0);
  const initialSnapshotRef = useRef(null);

  useEffect(() => {
    setCurrentPage('Library V2');
    setForceCollapse(true);
  }, [setCurrentPage, setForceCollapse]);

  const applyEditorState = (nextDraft, nextPersisted = nextDraft) => {
    dispatch({ type: 'LOAD_DRAFT', payload: nextDraft });
    initialSnapshotRef.current = cloneWorkbookV2Value(nextPersisted);
    setLocales(extractLocales(nextDraft?.translations ?? {}));
    setSnapshotVersion((version) => version + 1);
  };

  const applyLoadedWorkbook = (data) => {
    const normalizedWorkbook = normalizeWorkbookV2(data);

    applyEditorState(normalizedWorkbook);

    return normalizedWorkbook;
  };

  useEffect(() => {
    if (!urlParams.id) {
      return;
    }

    getDataAPI(`/workbook/v2/${urlParams.id}`)
      .then((response) => response.data)
      .then((data) => {
        const normalizedWorkbook = normalizeWorkbookV2(data);

        dispatch({ type: 'LOAD_DRAFT', payload: normalizedWorkbook });
        initialSnapshotRef.current = cloneWorkbookV2Value(normalizedWorkbook);
        setLocales(extractLocales(normalizedWorkbook.translations));
        setSnapshotVersion((version) => version + 1);
        setInitialized(true);
      });
  }, [urlParams.id]);

  const updatePersistedSnapshot = (updater) => {
    initialSnapshotRef.current = updater(cloneWorkbookV2Value(initialSnapshotRef.current));
    setSnapshotVersion((version) => version + 1);
  };

  const deferredDraft = useDeferredValue(draft);

  const savePlan = useMemo(
    () => buildWorkbookV2SavePlan(initialSnapshotRef.current, deferredDraft),
    [deferredDraft, snapshotVersion]
  );

  const ensureLocale = (locale) => {
    if (!locale) {
      return;
    }

    setLocales((currentLocales) => (currentLocales.includes(locale) ? currentLocales : [...currentLocales, locale]));
  };

  const handleWorkbookUpdate = (updates) => {
    dispatch({ type: 'UPDATE_WORKBOOK', updates });
  };

  const handleWorkbookTranslationUpdate = (key, value) => {
    const nextTranslations = updateTranslation(draft?.translations ?? {}, editingLanguage, key, value);
    dispatch({ type: 'UPDATE_WORKBOOK', updates: { translations: nextTranslations } });
  };

  const handleChapterUpdate = (chapterId, updates) => {
    dispatch({ type: 'UPDATE_CHAPTER', chapterId, updates });
  };

  const handleLessonUpdate = (chapterId, lessonId, updates) => {
    dispatch({ type: 'UPDATE_LESSON', chapterId, lessonId, updates });
  };

  const handleItemUpdate = (chapterId, lessonId, item) => {
    dispatch({ type: 'UPDATE_ITEM', chapterId, lessonId, item });
  };

  const handleSave = useCallback(() => {
    if (!draft || !savePlan.hasChanges) {
      return;
    }

    const saveChanges = async () => {
      let workingDraft = cloneWorkbookV2Value(draft);
      let persistedWorkbook = cloneWorkbookV2Value(initialSnapshotRef.current);

      try {
        let workingPlan = buildWorkbookV2SavePlan(persistedWorkbook, workingDraft);

        for (const { chapterId, lessonId, itemId } of workingPlan.deletes.items) {
          await deleteDataAPI(`/workbook/v2/${draft._id}/chapter/${chapterId}/lesson/${lessonId}/item/${itemId}`);
          persistedWorkbook = removeWorkbookItem(persistedWorkbook, chapterId, lessonId, itemId);
        }

        workingPlan = buildWorkbookV2SavePlan(persistedWorkbook, workingDraft);

        for (const { chapterId, lessonId } of workingPlan.deletes.lessons) {
          await deleteDataAPI(`/workbook/v2/${draft._id}/chapter/${chapterId}/lesson/${lessonId}`);
          persistedWorkbook = removeWorkbookLesson(persistedWorkbook, chapterId, lessonId);
        }

        workingPlan = buildWorkbookV2SavePlan(persistedWorkbook, workingDraft);

        for (const { chapterId } of workingPlan.deletes.chapters) {
          await deleteDataAPI(`/workbook/v2/${draft._id}/chapter/${chapterId}`);
          persistedWorkbook = removeWorkbookChapter(persistedWorkbook, chapterId);
        }

        workingPlan = buildWorkbookV2SavePlan(persistedWorkbook, workingDraft);

        for (const chapter of workingPlan.creates.chapters) {
          const previousChapters = persistedWorkbook.chapters ?? [];
          const response = await postDataAPI(`/workbook/v2/${draft._id}/chapter`, {
            title: chapter.title || 'New Chapter'
          });
          const serverChapters = response.data.chapters ?? [];
          const createdChapter = findCreatedEntity(serverChapters, previousChapters);

          if (!createdChapter?._id) {
            throw new Error('Failed to resolve created chapter');
          }

          workingDraft = replaceWorkbookTempChapter(workingDraft, chapter._id, createdChapter);
          persistedWorkbook = {
            ...persistedWorkbook,
            chapters: cloneWorkbookV2Value(serverChapters)
          };
        }

        workingPlan = buildWorkbookV2SavePlan(persistedWorkbook, workingDraft);

        for (const { chapterId, lesson } of workingPlan.creates.lessons) {
          const previousLessons = getChapterById(persistedWorkbook, chapterId)?.lessons ?? [];
          const response = await postDataAPI(`/workbook/v2/${draft._id}/chapter/${chapterId}/lesson`, {
            title: lesson.title || 'New Lesson'
          });
          const serverLessons =
            response.data.chapters?.find((chapter) => chapter._id === chapterId)?.lessons ?? [];
          const createdLesson = findCreatedEntity(serverLessons, previousLessons);

          if (!createdLesson?._id) {
            throw new Error('Failed to resolve created lesson');
          }

          workingDraft = replaceWorkbookTempLesson(workingDraft, chapterId, lesson._id, createdLesson);
          persistedWorkbook = setWorkbookChapterLessons(persistedWorkbook, chapterId, serverLessons);
        }

        workingPlan = buildWorkbookV2SavePlan(persistedWorkbook, workingDraft);

        for (const { chapterId, lessonId, item } of workingPlan.creates.items) {
          const previousItems = getLessonById(persistedWorkbook, chapterId, lessonId)?.items ?? [];
          const response = await postDataAPI(
            `/workbook/v2/${draft._id}/chapter/${chapterId}/lesson/${lessonId}/item`,
            buildWorkbookV2ItemCreatePayload(item)
          );
          const serverItems =
            response.data.chapters
              ?.find((chapter) => chapter._id === chapterId)
              ?.lessons.find((lesson) => lesson._id === lessonId)?.items ?? [];
          const createdItem = findCreatedEntity(serverItems, previousItems);

          if (!createdItem?._id) {
            throw new Error('Failed to resolve created item');
          }

          workingDraft = replaceWorkbookTempItem(workingDraft, chapterId, lessonId, item._id, createdItem);
          persistedWorkbook = setWorkbookLessonItems(persistedWorkbook, chapterId, lessonId, serverItems);
        }

        workingPlan = buildWorkbookV2SavePlan(persistedWorkbook, workingDraft);

        if (workingPlan.workbook) {
          await patchDataAPI(`/workbook/v2/${draft._id}`, workingPlan.workbook);
        }

        await Promise.all(
          workingPlan.chapters.map(({ chapterId, data }) =>
            patchDataAPI(`/workbook/v2/${draft._id}/chapter/${chapterId}`, data)
          )
        );

        await Promise.all(
          workingPlan.lessons.map(({ chapterId, lessonId, data }) =>
            patchDataAPI(`/workbook/v2/${draft._id}/chapter/${chapterId}/lesson/${lessonId}`, data)
          )
        );

        await Promise.all(
          workingPlan.items.map(({ chapterId, lessonId, itemId, data }) =>
            patchDataAPI(`/workbook/v2/${draft._id}/chapter/${chapterId}/lesson/${lessonId}/item/${itemId}`, data)
          )
        );

        persistedWorkbook = cloneWorkbookV2Value(workingDraft);

        const refreshedWorkbook = await getDataAPI(`/workbook/v2/${draft._id}`)
          .then((response) => response.data)
          .then((data) => applyLoadedWorkbook(data));

        setInitialized(!!refreshedWorkbook);
      } catch (error) {
        applyEditorState(workingDraft, persistedWorkbook);
        throw error;
      }
    };

    toast.promise(
      saveChanges(),
      {
        loading: 'Saving Changes…',
        success: 'Changes Saved',
        error: 'Failed to Save Changes'
      },
      { id: ONE_TOAST_ID }
    );
  }, [draft, savePlan]);

  const handleNewChapter = () => {
    dispatch({ type: 'ADD_CHAPTER', chapter: createWorkbookV2ChapterDraft() });
  };

  const handleDeleteChapter = (chapterId) => {
    dispatch({ type: 'DELETE_CHAPTER', chapterId });
  };

  const handleCreateLesson = (chapterId) => {
    const lesson = createWorkbookV2LessonDraft();
    dispatch({ type: 'ADD_LESSON', chapterId, lesson });
    return lesson;
  };

  const handleDeleteLesson = (chapterId, lessonId) => {
    dispatch({ type: 'DELETE_LESSON', chapterId, lessonId });
  };

  const handleCreateItem = (chapterId, lessonId, item) => {
    const nextItem = createWorkbookV2ItemDraft(item);
    dispatch({ type: 'ADD_ITEM', chapterId, lessonId, item: nextItem });
    return nextItem;
  };

  const handleDeleteItem = (chapterId, lessonId, itemId) => {
    dispatch({ type: 'DELETE_ITEM', chapterId, lessonId, itemId });
  };

  const handleUploadItemImage = async (chapterId, lessonId, itemId, acceptedFiles) => {
    if ([chapterId, lessonId, itemId].some((id) => isWorkbookV2TempId(id))) {
      toast.error('Save changes before uploading an image to a new item.', { id: ONE_TOAST_ID });
      return null;
    }

    const formData = new FormData();
    formData.append('image', acceptedFiles[0]);
    formData.append('type', 'image');

    return await toast.promise(
      patchDataAPI(
        `/workbook/v2/${draft._id}/chapter/${chapterId}/lesson/${lessonId}/item/${itemId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      ).then((response) => {
        const serverItem =
          response.data.chapters
            ?.find((chapter) => chapter._id === chapterId)
            ?.lessons.find((lesson) => lesson._id === lessonId)
            ?.items.find((item) => item._id === itemId) ?? null;

        if (!serverItem) {
          return null;
        }

        const draftItem = draft.chapters
          .find((chapter) => chapter._id === chapterId)
          ?.lessons.find((lesson) => lesson._id === lessonId)
          ?.items.find((item) => item._id === itemId);

        dispatch({
          type: 'UPDATE_ITEM',
          chapterId,
          lessonId,
          item: {
            ...draftItem,
            options: {
              ...(draftItem?.options ?? {}),
              image_path: serverItem.options?.image_path
            }
          }
        });

        updatePersistedSnapshot((persistedWorkbook) => ({
          ...persistedWorkbook,
          chapters: updateItemById(
            persistedWorkbook.chapters ?? [],
            chapterId,
            lessonId,
            itemId,
            () => cloneWorkbookV2Value(serverItem)
          )
        }));

        return serverItem;
      }),
      {
        loading: 'Uploading File',
        success: 'Uploaded File',
        error: 'Failed to Upload File'
      },
      { id: ONE_TOAST_ID }
    );
  };

  const handleLessonTranslated = (chapterId, translatedLesson) => {
    const draftLesson = draft.chapters
      .find((chapter) => chapter._id === chapterId)
      ?.lessons.find((lesson) => lesson._id === translatedLesson._id);

    dispatch({
      type: 'REPLACE_LESSON',
      chapterId,
      lesson: mergeTranslatedLessonWithDraft(draftLesson, translatedLesson)
    });

    updatePersistedSnapshot((persistedWorkbook) => ({
      ...persistedWorkbook,
      chapters: updateLessonById(
        persistedWorkbook.chapters ?? [],
        chapterId,
        translatedLesson._id,
        () => cloneWorkbookV2Value(translatedLesson)
      )
    }));
  };

  const handleUploadCoverImage = (acceptedFiles) => {
    const formData = new FormData();
    formData.append('cover_image', acceptedFiles[0]);

    toast.promise(
      patchDataAPI(`/workbook/v2/${draft._id}`, formData).then((response) => {
        dispatch({ type: 'SET_COVER_IMAGE', coverImage: response.data.cover_image });

        updatePersistedSnapshot((persistedWorkbook) => ({
          ...persistedWorkbook,
          cover_image: response.data.cover_image
        }));

        setEditImage(false);
      }),
      {
        loading: 'Uploading Cover Image',
        success: 'Uploaded Cover Image',
        error: 'Failed to Upload Cover Image'
      },
      { id: ONE_TOAST_ID }
    );
  };

  useEffect(() => {
    const handleKeydown = (event) => {
      const isSaveShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's';

      if (!isSaveShortcut) {
        return;
      }

      event.preventDefault();
      handleSave();
    };

    window.addEventListener('keydown', handleKeydown);

    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  }, [handleSave]);

  if (!initialized || !draft) {
    return <Loading />;
  }

  return (
    <>
      <div className="flex w-full flex-col items-center">
        <span className="self-start">Workbook ID: {draft._id}</span>
        <div
          className={`grid ${editingLanguage ? 'grid-cols-2' : 'grid-cols-1'} justify-items-center
            gap-3 lg:w-3/4`}
        >
          <div className="grid grid-cols-[1fr_3fr] gap-10">
            <div className="relative col-start-1 row-span-3 min-w-max rounded-2xl p-4 shadow-md">
              {editImage ? (
                <Dropzone
                  onDrop={(acceptedFiles) => handleUploadCoverImage(acceptedFiles)}
                  accept={{ 'image/jpeg': ['.jpeg', '.png'] }}
                >
                  {({ getRootProps, getInputProps }) => (
                    <section>
                      <div
                        {...getRootProps()}
                        className="border-1 border-dashed border-gray-200 py-30"
                      >
                        <input {...getInputProps()} />
                        <span className="p-5">Drop or Click Here</span>
                      </div>
                    </section>
                  )}
                </Dropzone>
              ) : (
                <img
                  src={draft.cover_image ? `${process.env.REACT_APP_API_URL}/${draft.cover_image}` : placeholder}
                  alt="Cover"
                  className="size-80"
                />
              )}
              <PencilSquareIcon
                className="absolute right-0 bottom-0 size-15 rounded-tl-md bg-white p-2
                  hover:cursor-pointer"
                onClick={() => setEditImage(!editImage)}
              ></PencilSquareIcon>
            </div>
            <OneLineInput
              className="col-start-2"
              value={draft.title}
              label="Title"
              onChange={(value) => handleWorkbookUpdate({ title: value })}
            ></OneLineInput>
            <OneLineInput
              className="col-start-2"
              value={draft.description}
              label="Description"
              onChange={(value) => handleWorkbookUpdate({ description: value })}
            ></OneLineInput>
            <div className="flex items-center gap-3">
              <OneLineInput
                className="col-start-2"
                value={draft.sku}
                label="SKU"
                onChange={(value) => handleWorkbookUpdate({ sku: value })}
              ></OneLineInput>
              <Button
                variant="primary"
                className="whitespace-nowrap"
                onClick={() => setShowNewTranslationModal(true)}
              >
                Add/Edit Translation
              </Button>
            </div>
          </div>
          {editingLanguage && (
            <div className="flex w-3/4 flex-col gap-10">
              <div className="-mt-14 flex justify-between">
                <div>
                  Editing Language: <span className="text-3xl font-bold">{editingLanguage}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={() => setEditingLanguage(null)}>
                    <XMarkIcon className="mr-2 size-8" />
                  </Button>
                </div>
              </div>

              <OneLineInput
                value={draft.translations?.title?.find((entry) => entry.locale === editingLanguage)?.text}
                label="Title"
                onChange={(value) => handleWorkbookTranslationUpdate('title', value)}
              ></OneLineInput>
              <OneLineInput
                value={draft.translations?.description?.find((entry) => entry.locale === editingLanguage)?.text}
                label="Description"
                onChange={(value) => handleWorkbookTranslationUpdate('description', value)}
              ></OneLineInput>
              <OneLineInput
                value={draft.translations?.sku?.find((entry) => entry.locale === editingLanguage)?.text}
                label="SKU"
                onChange={(value) => handleWorkbookTranslationUpdate('sku', value)}
              ></OneLineInput>
            </div>
          )}
          <Accordion
            className={`flex w-5/6 flex-col items-center gap-5
              ${editingLanguage ? 'col-span-2 w-full' : 'col-span-1'}`}
            allowZeroExpanded
          >
            <SortableList
              items={draft.chapters ?? []}
              onChange={(chapters) => dispatch({ type: 'SET_CHAPTERS', chapters })}
              renderItem={(chapter) => (
                <SortableList.Item id={chapter._id}>
                  <div className="w-full">
                    <WorkbookV2Chapter
                      chapter={chapter}
                      workbook={draft}
                      deleteChapter={handleDeleteChapter}
                      editingLanguage={editingLanguage}
                      onUpdateChapter={handleChapterUpdate}
                      onCreateLesson={handleCreateLesson}
                      onDeleteLesson={handleDeleteLesson}
                      onLessonsReorder={(chapterId, lessons) =>
                        dispatch({ type: 'SET_CHAPTER_LESSONS', chapterId, lessons })
                      }
                      onUpdateLesson={handleLessonUpdate}
                      onCreateItem={handleCreateItem}
                      onDeleteItem={handleDeleteItem}
                      onUploadItemImage={handleUploadItemImage}
                      onUpdateItem={handleItemUpdate}
                      onLessonTranslated={handleLessonTranslated}
                    />
                  </div>
                </SortableList.Item>
              )}
            />
            <div className="group flex items-center hover:cursor-pointer" onClick={handleNewChapter}>
              <PlusCircleIcon
                className="text-pdteal size-12 self-center drop-shadow-md hover:cursor-pointer"
              />
              <div
                className="block w-0 overflow-hidden whitespace-nowrap transition-[width]
                  duration-500 group-hover:w-50"
              >
                Add a Chapter
              </div>
            </div>
          </Accordion>
        </div>
      </div>
      <div
        className={`fixed top-40 right-15 z-50 transition-all duration-200
          ${savePlan.hasChanges ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}`}
      >
        <div
          className="bg-pdteal/50 pointer-events-none absolute top-1/2 left-1/2 h-10 w-30
            -translate-1/2 animate-ping rounded-lg"
        ></div>
        <Button
          variant="new-primary"
          className="whitespace-nowrap shadow-lg"
          onClick={handleSave}
          disabled={!savePlan.hasChanges}
        >
          Save Changes (Cmd/Ctrl+S)
        </Button>
      </div>

      {showNewTranslationModal && (
        <SimpleModal hideModal={() => setShowNewTranslationModal(false)}>
          <div className="flex w-full flex-col items-center gap-8">
            Edit an existing language:
            <div className="flex items-center gap-8">
              {locales.map((locale, index) => (
                <button
                  className={`bg-pdteal hover:bg-pdteal/80 ml-2 cursor-pointer rounded-2xl px-4 py-2
                  text-white`}
                  key={locale + '' + index}
                  onClick={() => {
                    setEditingLanguage(locale);
                    setShowNewTranslationModal(false);
                  }}
                >
                  {locale}
                </button>
              ))}
            </div>
            <span className="pb-8">or Add a new one:</span>
            <div className="ml-25 flex w-1/3 items-center gap-8">
              <OneLineInput label="Locale" value={pendingLocale} onChange={setPendingLocale} />
              <button
                className={`bg-pdteal hover:bg-pdteal/80 ml-2 aspect-square cursor-pointer
                rounded-2xl px-4 py-2 text-white`}
                onClick={() => {
                  const nextLocale = pendingLocale.trim();

                  if (!nextLocale) {
                    return;
                  }

                  ensureLocale(nextLocale);
                  setEditingLanguage(nextLocale);
                  setPendingLocale('');
                  setShowNewTranslationModal(false);
                }}
              >
                <ArrowRightIcon className="size-8" />
              </button>
            </div>
          </div>
        </SimpleModal>
      )}
    </>
  );
};
